import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  CreateFlightDealData,
  createFlightDeal,
  deleteFlightDeal,
  getFlightDeal,
  updateFlightDeal,
} from '~/services/FlightsService';

export const FLIGHT_DEAL_QUERY_KEY = 'flightDeal';

export function useFlightDeal(id: string) {
  const {
    data,
    isLoading: loading,
    error,
    refetch,
  } = useQuery<App.FlightDeal, Error>({
    queryKey: [FLIGHT_DEAL_QUERY_KEY, id],
    queryFn: async () => {
      const { result } = await getFlightDeal(id);
      return result;
    },
    enabled: !!id,
  });

  return { loading, error, data, refetch };
}

type UpdateFlightDealParams = {
  id: string;
  data: CreateFlightDealData;
};

export function useUpdateFlightDeal() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const mutation = useMutation({
    mutationFn: ({ id, data }: UpdateFlightDealParams) => updateFlightDeal(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [FLIGHT_DEAL_QUERY_KEY, id] });
      enqueueSnackbar('Deal was updated', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Something went wrong, please try again', { variant: 'error' });
    },
  });

  return {
    mutate: mutation.mutate,
    loading: mutation.isPending,
    error: mutation.error,
  };
}

export function useDeleteFlightDeal() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const mutation = useMutation({
    mutationFn: (dealId: string) => deleteFlightDeal(dealId),
    onSuccess: (_, dealId) => {
      queryClient.invalidateQueries({ queryKey: [FLIGHT_DEAL_QUERY_KEY, dealId] });
      enqueueSnackbar('Deal was deleted', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Something went wrong, please try again', { variant: 'error' });
    },
  });

  return {
    mutate: mutation.mutate,
    loading: mutation.isPending,
    error: mutation.error,
  };
}

export function useCreateFlightDeal() {
  const mutation = useMutation({
    mutationFn: (data: CreateFlightDealData) => createFlightDeal(data),
  });

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
